import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../App";

const KmApprovalPage = () => {
  const [teamData, setTeamData] = useState({ subordinates: [], logs: [], summary: {} });
  const [alerts, setAlerts] = useState({ total_alerts: 0, critical: 0, alerts: [] });
  const [loading, setLoading] = useState(true);
  const [selectedLogs, setSelectedLogs] = useState([]);
  const [activeTab, setActiveTab] = useState("team");
  const [filters, setFilters] = useState({ status: "", start_date: "", end_date: "" });
  
  const { user } = useAuth();
  const isHRAdmin = ["super_admin", "tenant_admin", "hr_manager"].includes(user?.role);

  useEffect(() => {
    fetchData();
  }, [filters, activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      if (activeTab === "team") {
        const teamRes = await api.get(`/hr/km-maps/my-team?${params.toString()}`);
        setTeamData(teamRes.data);
      } else if (activeTab === "alerts" && isHRAdmin) {
        const alertsRes = await api.get("/hr/km-maps/alerts");
        setAlerts(alertsRes.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status !== 403) {
        toast.error("Erro ao carregar dados");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleManagerApprove = async (logId, action) => {
    try {
      await api.put(`/hr/km-maps/${logId}/manager-approve?action=${action}`);
      toast.success(action === "approve" ? "Registo aprovado pela chefia" : "Registo rejeitado");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao processar registo");
    }
  };

  const handleBulkManagerApprove = async (action) => {
    if (selectedLogs.length === 0) {
      toast.error("Selecione pelo menos um registo");
      return;
    }
    try {
      await api.put("/hr/km-maps/bulk-manager-approve", {
        log_ids: selectedLogs,
        action: action
      });
      toast.success(`${selectedLogs.length} registos ${action === "approve" ? "aprovados" : "rejeitados"} pela chefia`);
      setSelectedLogs([]);
      fetchData();
    } catch (error) {
      toast.error("Erro ao processar registos");
    }
  };

  const handleSendReminder = async (logIds, type) => {
    try {
      await api.post("/hr/km-maps/send-reminder", {
        log_ids: logIds,
        type: type
      });
      toast.success("Lembretes enviados com sucesso");
    } catch (error) {
      toast.error("Erro ao enviar lembretes");
    }
  };

  const handleExportPDF = async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      const response = await api.get(`/hr/km-maps/export/pdf?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `mapa_km_equipa_${new Date().toISOString().split("T")[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF exportado com sucesso");
    } catch (error) {
      toast.error("Erro ao exportar PDF");
    }
  };

  const toggleSelectLog = (logId) => {
    setSelectedLogs(prev => 
      prev.includes(logId) ? prev.filter(id => id !== logId) : [...prev, logId]
    );
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { label: "Pendente", color: "bg-amber-100 text-amber-700" },
      pending_manager: { label: "Aguarda Chefia", color: "bg-orange-100 text-orange-700" },
      manager_approved: { label: "Aprovado Chefia", color: "bg-blue-100 text-blue-700" },
      approved: { label: "Aprovado RH", color: "bg-emerald-100 text-emerald-700" },
      rejected: { label: "Rejeitado", color: "bg-red-100 text-red-700" }
    };
    const c = config[status] || { label: status, color: "bg-gray-100 text-gray-700" };
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${c.color}`}>{c.label}</span>;
  };

  const getPriorityBadge = (priority, days) => {
    if (priority === "high" || days > 15) {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">Crítico ({days} dias)</span>;
    }
    return <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">{days} dias</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="km-approval-page" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans']">Aprovação de Quilómetros</h1>
          <p className="text-muted-foreground">Aprove os registos de KM da sua equipa</p>
        </div>
        <button
          onClick={handleExportPDF}
          className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
          Exportar PDF
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border">
        <button
          onClick={() => setActiveTab("team")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "team" 
              ? "border-primary text-primary" 
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          Minha Equipa
          {teamData.summary?.pending > 0 && (
            <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700">
              {teamData.summary.pending}
            </span>
          )}
        </button>
        {isHRAdmin && (
          <button
            onClick={() => setActiveTab("alerts")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "alerts" 
                ? "border-primary text-primary" 
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            Alertas
            {alerts.total_alerts > 0 && (
              <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700">
                {alerts.total_alerts}
              </span>
            )}
          </button>
        )}
      </div>

      {/* Team Tab */}
      {activeTab === "team" && (
        <>
          {/* Summary */}
          {teamData.subordinates?.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="rounded-xl border bg-card p-4">
                <p className="text-2xl font-bold">{teamData.summary?.total || 0}</p>
                <p className="text-xs text-muted-foreground">Total Registos</p>
              </div>
              <div className="rounded-xl border bg-card p-4">
                <p className="text-2xl font-bold text-amber-600">{teamData.summary?.pending || 0}</p>
                <p className="text-xs text-muted-foreground">Aguardam Aprovação</p>
              </div>
              <div className="rounded-xl border bg-card p-4">
                <p className="text-2xl font-bold text-blue-600">{teamData.summary?.approved || 0}</p>
                <p className="text-xs text-muted-foreground">Aprovados</p>
              </div>
              <div className="rounded-xl border bg-card p-4">
                <p className="text-2xl font-bold text-red-600">{teamData.summary?.rejected || 0}</p>
                <p className="text-xs text-muted-foreground">Rejeitados</p>
              </div>
            </div>
          )}

          {/* Subordinates */}
          {teamData.subordinates?.length > 0 && (
            <div className="rounded-xl border bg-card p-4">
              <h3 className="text-sm font-medium mb-3">Colaboradores da sua equipa:</h3>
              <div className="flex flex-wrap gap-2">
                {teamData.subordinates.map((sub) => (
                  <span key={sub.employee_id} className="px-3 py-1 rounded-full bg-muted text-sm">
                    {sub.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="rounded-xl border bg-card p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-medium mb-1">Estado</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background text-sm"
                >
                  <option value="">Todos</option>
                  <option value="pending">Pendentes</option>
                  <option value="pending_manager">Aguarda Chefia</option>
                  <option value="manager_approved">Aprovados</option>
                  <option value="rejected">Rejeitados</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Data Início</label>
                <input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Data Fim</label>
                <input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background text-sm"
                />
              </div>
            </div>
          </div>

          {/* Bulk Actions */}
          {selectedLogs.length > 0 && (
            <div className="flex items-center gap-4 p-4 rounded-xl bg-indigo-50 border border-indigo-200">
              <span className="text-sm font-medium text-indigo-700">
                {selectedLogs.length} registo(s) selecionado(s)
              </span>
              <button
                onClick={() => handleBulkManagerApprove("approve")}
                className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 text-sm"
              >
                Aprovar Selecionados
              </button>
              <button
                onClick={() => handleBulkManagerApprove("reject")}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 text-sm"
              >
                Rejeitar Selecionados
              </button>
              <button
                onClick={() => setSelectedLogs([])}
                className="px-4 py-2 rounded-lg border border-input hover:bg-muted text-sm"
              >
                Limpar
              </button>
            </div>
          )}

          {/* Team KM Logs Table */}
          {teamData.subordinates?.length === 0 ? (
            <div className="text-center py-12 bg-card rounded-xl border">
              <svg className="w-12 h-12 mx-auto text-muted-foreground mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <p className="text-muted-foreground font-medium">Não tem colaboradores atribuídos</p>
              <p className="text-sm text-muted-foreground mt-1">Os registos de KM dos seus subordinados aparecerão aqui</p>
            </div>
          ) : teamData.logs?.length === 0 ? (
            <div className="text-center py-12 bg-card rounded-xl border">
              <svg className="w-12 h-12 mx-auto text-muted-foreground mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-muted-foreground">Nenhum registo de KM pendente</p>
            </div>
          ) : (
            <div className="rounded-xl border bg-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-4 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectedLogs.length === teamData.logs.filter(l => ["pending", "pending_manager"].includes(l.status)).length && selectedLogs.length > 0}
                          onChange={() => {
                            const pendingIds = teamData.logs.filter(l => ["pending", "pending_manager"].includes(l.status)).map(l => l.log_id);
                            setSelectedLogs(selectedLogs.length === pendingIds.length ? [] : pendingIds);
                          }}
                          className="rounded border-gray-300"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Data</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Colaborador</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Viatura</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Trajeto</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">KM</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Estado</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {teamData.logs.map((log) => (
                      <tr key={log.log_id} className="hover:bg-muted/30">
                        <td className="px-4 py-3">
                          {["pending", "pending_manager"].includes(log.status) && (
                            <input
                              type="checkbox"
                              checked={selectedLogs.includes(log.log_id)}
                              onChange={() => toggleSelectLog(log.log_id)}
                              className="rounded border-gray-300"
                            />
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">{log.date}</td>
                        <td className="px-4 py-3 text-sm font-medium">{log.employee_name}</td>
                        <td className="px-4 py-3 text-sm">{log.license_plate || "-"}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className="text-muted-foreground">{log.origin || "-"}</span>
                          <span className="mx-1">→</span>
                          <span>{log.destination || "-"}</span>
                        </td>
                        <td className="px-4 py-3 text-sm font-bold text-indigo-600">
                          {(log.km_difference || log.distance || 0).toLocaleString("pt-PT")} km
                        </td>
                        <td className="px-4 py-3">{getStatusBadge(log.status)}</td>
                        <td className="px-4 py-3">
                          {["pending", "pending_manager"].includes(log.status) && (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleManagerApprove(log.log_id, "approve")}
                                className="p-1.5 rounded-lg bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                                title="Aprovar"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleManagerApprove(log.log_id, "reject")}
                                className="p-1.5 rounded-lg bg-red-100 text-red-700 hover:bg-red-200"
                                title="Rejeitar"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          )}
                          {log.manager_approved_by_name && (
                            <span className="text-xs text-muted-foreground">Por: {log.manager_approved_by_name}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Alerts Tab (HR Admin only) */}
      {activeTab === "alerts" && isHRAdmin && (
        <>
          {/* Alerts Summary */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="rounded-xl border bg-card p-4">
              <p className="text-2xl font-bold text-amber-600">{alerts.total_alerts}</p>
              <p className="text-xs text-muted-foreground">Registos Pendentes (+10 dias)</p>
            </div>
            <div className="rounded-xl border bg-card p-4">
              <p className="text-2xl font-bold text-red-600">{alerts.critical}</p>
              <p className="text-xs text-muted-foreground">Críticos (+15 dias)</p>
            </div>
            <div className="rounded-xl border bg-card p-4 flex items-center gap-4">
              <button
                onClick={() => handleSendReminder(alerts.alerts.map(a => a.log_id), "employee")}
                className="px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-sm"
                disabled={alerts.alerts.length === 0}
              >
                Lembrar Colaboradores
              </button>
              <button
                onClick={() => handleSendReminder(alerts.alerts.map(a => a.log_id), "manager")}
                className="px-3 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700 text-sm"
                disabled={alerts.alerts.length === 0}
              >
                Lembrar Chefias
              </button>
            </div>
          </div>

          {/* Alerts Table */}
          {alerts.alerts.length === 0 ? (
            <div className="text-center py-12 bg-card rounded-xl border">
              <svg className="w-12 h-12 mx-auto text-emerald-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-muted-foreground font-medium">Sem alertas</p>
              <p className="text-sm text-muted-foreground mt-1">Não existem registos de KM pendentes há mais de 10 dias</p>
            </div>
          ) : (
            <div className="rounded-xl border bg-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Data</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Colaborador</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Chefia</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Destino</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">KM</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Tempo Pendente</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {alerts.alerts.map((alert) => (
                      <tr key={alert.log_id} className={`hover:bg-muted/30 ${alert.priority === "high" ? "bg-red-50" : ""}`}>
                        <td className="px-4 py-3 text-sm">{alert.date}</td>
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium">{alert.employee_name}</p>
                          <p className="text-xs text-muted-foreground">{alert.employee_email}</p>
                        </td>
                        <td className="px-4 py-3 text-sm">{alert.manager_name || <span className="text-muted-foreground">Sem chefia</span>}</td>
                        <td className="px-4 py-3 text-sm">{alert.destination || "-"}</td>
                        <td className="px-4 py-3 text-sm font-bold text-indigo-600">
                          {(alert.km_difference || 0).toLocaleString("pt-PT")} km
                        </td>
                        <td className="px-4 py-3">{getPriorityBadge(alert.priority, alert.days_pending)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleSendReminder([alert.log_id], "employee")}
                              className="p-1.5 rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200"
                              title="Lembrar Colaborador"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                              </svg>
                            </button>
                            {alert.manager_name && (
                              <button
                                onClick={() => handleSendReminder([alert.log_id], "manager")}
                                className="p-1.5 rounded-lg bg-purple-100 text-purple-700 hover:bg-purple-200"
                                title="Lembrar Chefia"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default KmApprovalPage;
